import apiInstance, { cachedApiInstance } from "api";
import dayjs from "dayjs";
import { splitTimeInterval } from "helpers";
import { apiInstanceFileUpload } from "api";
import TimeSeriesService from "api/timeseries.api";

const utc = require("dayjs/plugin/utc");

const normalizeTimeSeriesResponse = (response) => {
  if (!response || !response.data || !response.data.data) return response;

  const payload = response.data.data;
  if (payload && typeof payload === "object") {
    response.data = {
      ...response.data,
      ...payload,
    };
  }

  return response;
};

export const sendMagicLink = async (body) => {
  return await apiInstance.post("/admin/users/send/login-link", body);
};

export const authenticatorSetup = async (body) => {
  return await apiInstance.get("/users/2fa/authenticator/setup");
};

// The first code from the app proves it was set up; this turns the app on.
export const completeAuthenticatorSetup = async (code) => {
  return await apiInstance.post("/users/2fa/authenticator/setup/complete", { code });
};

export const addPhoneNumber = async (body) => {
  return await apiInstance.post("/users/add-phone", body);
};

// Self-service app-mode preference (drives the AppModeSwitcher in the navbar).
// `appMode` must be one of "flex2" / "afms" / "lcp" — server rejects others.
// `null` / "" clears the preference and reverts to the computed default.
export const setMyAppMode = async (appMode) => {
  return await apiInstance.put("/users/app-mode", { appMode });
};

// Admin (super-admin or org-owner): edit a target user's login handles.
// Capability is inferred at request time from what handles are on file —
// there is no per-user "mode". Body shape:
//   email?:    nullable; "" wipes
//   phone?:    nullable; "" wipes; E.164 expected
//   username?: nullable; "" wipes
//   tempPassword?: Keycloak temp password (user changes on first login)
//   requireOtpBind?: when phone is set, fires a Twilio Verify start so
//                    the user proves possession before the next login
export const setUserAuthMethod = async (userId, body) => {
  return await apiInstance.put(`/users/${userId}/auth-method`, body);
};

// Admin: generate a fresh temporary password for a user and EMAIL it to them
// (they log in with it and are forced to change it on first login). Requires a
// real email + a linked login account server-side. -> { data: { message, data: { email } } }
export const resetUserPassword = async (userId) => {
  return await apiInstance.post(`/users/${userId}/reset-password`, {});
};

// Self-service per-system pinned home route. systemName is the
// OrganizationSystem catalog name ("Environmental" / "Fixed Operations" /
// "Energy"). route is the in-app path the user pins; null/empty clears
// the pin (user falls back to the global default for that system).
// Returns once the row is upserted (or removed) on the server.
export const setMySystemDefaultRoute = async (systemName, route) => {
  return await apiInstance.put("/users/me/system-default", { systemName, route });
};

export const sendMobileCode = async (body = {}) => {
  return await apiInstance.post("/users/send-mobile-verification-code", body);
};

export const verifyMobileCode = async (body) => {
  return await apiInstance.post("/users/verify-mobile-code", body);
};

export const getUserInfo = async (body) => {
  return await apiInstance.post("/auth/user-info", body);
};

export const disableMobile2Fa = async (body = {}) => {
  return await apiInstance.post("/users/disable-mobile-2fa", body);
};


// Runs before the login finishes, so the token from signing in with the
// temporary password is not in localStorage yet; the API requires it.
export const changeTempPassword = async (body, authToken) => {
  return await apiInstance.post(
    "/auth/change-temp-password",
    body,
    authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined,
  );
};

export const fetchLatestChartData = (
  deviceNumber,
  { type, dataSource, timeKey, requestOpts },
  source = null
) => {
  const dataSources = Array.isArray(dataSource) ? dataSource : [dataSource];
  return TimeSeriesService.getLatest(
    deviceNumber,
    {
      deviceType: type,
      timeKey: timeKey,
      dataSources: dataSources,
    },
    source?.token ? { signal: source.token } : undefined
  ).then(normalizeTimeSeriesResponse);
};

export const makeTimeFilter = (timeInterval, start, end, timeKey) => {
  let timeFilter = "";

  if (timeInterval === "custom") {
    if (!start || !end) return;
    timeFilter = `AND ${timeKey} BETWEEN '${start.toISOString()}' AND '${end.toISOString()}'`;
  } else {
    const { value, unit } = splitTimeInterval(timeInterval);
    console.log("Data time", dayjs().toISOString());
    const date = dayjs().utc().subtract(value, unit).toISOString();
    timeFilter = `AND ${timeKey} IN '${date};${timeInterval}'`;
  }

  return timeFilter;
};

export const fetchChart = (
  deviceNumber,
  { type, timeInterval, start, end, dataSource, sample, timeKey, requestOpts }
) => {
  if (!timeInterval || !dataSource || dataSource.length === 0) return;

  const dataSources = Array.isArray(dataSource) ? dataSource : [dataSource];
  return TimeSeriesService.getChart(deviceNumber, {
    deviceType: type,
    timeKey: timeKey,
    dataSources: dataSources,
    timeInterval: timeInterval,
    startDate: start?.toISOString?.() || start,
    endDate: end?.toISOString?.() || end,
    sampleBy: sample,
    channelId: requestOpts?.channelId,
  }).then(normalizeTimeSeriesResponse);
};

export const fetchGridCount = (
  deviceNumber,
  { type, timeInterval, start, end, dataSource, sample, timeKey, startOffset = 0, endOffset = 100 }
) => {
  if (!timeInterval || !dataSource || dataSource.length === 0) return;

  const dataSources = Array.isArray(dataSource) ? dataSource : [dataSource];
  return TimeSeriesService.getGrid(deviceNumber, {
    deviceType: type,
    timeKey: timeKey,
    dataSources: dataSources,
    timeInterval: timeInterval,
    startDate: start?.toISOString?.() || start,
    endDate: end?.toISOString?.() || end,
    page: 1,
    pageSize: 1,
    includeCount: true,
  }).then(response => {
    const normalized = normalizeTimeSeriesResponse(response);
    const totalCount = normalized?.data?.totalCount ?? 0;
    return {
      data: {
        dataset: [[totalCount]]
      }
    };
  });
};

export const fetchGrid = (
  deviceNumber,
  { type, timeInterval, start, end, dataSource, sample, timeKey, startOffset = 0, endOffset = 100 }
) => {
  if (!timeInterval || !dataSource || dataSource.length === 0) return;

  const dataSources = Array.isArray(dataSource) ? dataSource : [dataSource];
  const pageSize = endOffset - startOffset || 20;
  const page = Math.floor(startOffset / pageSize) + 1;

  return TimeSeriesService.getGrid(deviceNumber, {
    deviceType: type,
    timeKey: timeKey,
    dataSources: dataSources,
    timeInterval: timeInterval,
    startDate: start?.toISOString?.() || start,
    endDate: end?.toISOString?.() || end,
    page: page,
    pageSize: pageSize,
    includeCount: false,
  }).then(response => {
    const normalized = normalizeTimeSeriesResponse(response);
    const payload = normalized?.data || {};
    return {
      data: {
        columns: payload.columns || [],
        dataset: payload.rows || payload.dataset || []
      }
    };
  });
};

export const fetchDeviceGrid = ({ deviceNumbers, type, dataSources, timeKey, order, orderBy }) => {
  if (!Array.isArray(deviceNumbers) || !type || !timeKey || !Array.isArray(dataSources)) return;

  return TimeSeriesService.getMultiDeviceLatest({
    deviceNumbers: deviceNumbers,
    deviceType: type,
    timeKey: timeKey,
    dataSources: dataSources,
    orderBy: orderBy || timeKey,
    order: order || "DESC",
  }).then(response => {
    const normalized = normalizeTimeSeriesResponse(response);
    const payload = normalized?.data || {};
    return {
      data: {
        columns: payload.columns || [],
        dataset: payload.rows || payload.dataset || []
      }
    };
  });
};

export const enegryMeterMonthlyReadingsApi = async (deivce, fitlers, requestOpts) => {
  return cachedApiInstance.get(`/devices/${deivce}/energy-meter/monthly/readings`, {
    requestOpts,
    params: {
      ...fitlers,
    },
  });
};

export const enegryMeterMonthlyConsumptionApi = async (deivce, fitlers, requestOpts) => {
  return cachedApiInstance.get(`/devices/${deivce}/energy-meter/monthly/consumption`, {
    ...requestOpts,
    params: {
      ...fitlers,
    },
  });
};

export const getSensorCategoriesApi = async () => {
  return await apiInstance.get(`/assets/sensor-categories`);
};

export const UserService = {
  impersonateUser: async (body) => {
    return await apiInstance.post("/auth/impersonate", body);
  },
  chooseSubdomain: async (body) => {
    return await apiInstance.post("/registeration/subdomain", body);
  },
};

export const AssetService = {
  createAsset: async (body) => {
    return await apiInstance.post("/assets", body);
  },
  // `config` is merged into the axios request so a caller can supply its own
  // paramsSerializer. Needed for list params like locationIds: axios's default
  // emits `locationIds[]=1`, which ASP.NET's List<int> query binder does not
  // read, so the filter would be dropped in silence and the caller would get
  // an unscoped page back believing it was scoped.
  getUserAssets: async (fitlers, config = {}) => {
    return await apiInstance.get(`/assets`, {
      params: {
        ...fitlers,
      },
      ...config,
    });
  },
  getUserDevices: async (queryParams = {}) => {
    return await apiInstance.get(`/devices`, {
      params: {
        ...queryParams,
      },
    });
  },
  deleteAsset: async (assetId) => {
    return await apiInstance.delete(`/assets/${assetId}`);
  },
  getUserAssetBySource: async (fitlers) => {
    return await apiInstance.get(`/assets/by-data-source`, {
      params: {
        ...fitlers,
      },
    });
  },
  createAssetLabel: async (body) => {
    return await apiInstance.post("/assets/label", body);
  },
  EditAssetLabel: async (body) => {
    return await apiInstance.put("/assets/label", body);
  },
  getUserAsset: async (systemId) => {
    return await apiInstance.get(`/assets/${systemId}`);
  },
  editAsset: async (body) => {
    return await apiInstance.post(`/assets/edit`, body);
  },
  // Energy-tab device classification round-trip. Pass null to clear.
  setEnergyDeviceKind: async (assetId, energyDeviceKind) => {
    return await apiInstance.patch(`/assets/${assetId}/energy-device-kind`, {
      energyDeviceKind,
    });
  },
  editDoorLabel: async (body) => {
    return await apiInstance.post(`/assets/door/label`, body);
  },
  getDoorLabels: async (deviceNumber) => {
    return await apiInstance.get(`/assets/door/labels/${deviceNumber}`);
  },
  getSensors: async (deviceId) => {
    return await apiInstance.get(`/assets/userassets-bydeviceid?deviceId=${deviceId}`);
  },
  createDevice: async (body) => {
    return await apiInstanceFileUpload.post("/assets/device", body);
  },
  createItem: async (body, config) => {
    return await apiInstanceFileUpload.post("/assets/item", body, config);
  },
  getDeviceSensors: async (deviceNumber) => {
    return await apiInstance.get(`/assets/${deviceNumber}/sensors`);
  },
  createSensor: async (body) => {
    return await apiInstanceFileUpload.post("/assets/sensor", body);
  },
  deleteFile: async (fileId) => {
    return await apiInstance.delete(`/assets/file/${fileId}`);
  },
  editItemAsset: async (assetId, formData) => {
    return await apiInstanceFileUpload.put(`/assets/item/${assetId}`, formData);
  },
  editDeviceAsset: async (assetId, formData) => {
    return await apiInstanceFileUpload.put(`/assets/device/${assetId}`, formData);
  },
  editSensorAsset: async (assetId, formData) => {
    return await apiInstanceFileUpload.put(`/assets/sensor/${assetId}`, formData);
  },
  addLinkedAsset: async (body) => {
    return await apiInstance.post(`/assets/linked-assets`, body);
  },
  getLinkedAssets: async (fitlers) => {
    return await apiInstance.get(`/assets/linked-assets`, {
      params: {
        ...fitlers,
      },
    });
  },
  deleteLinkedAsset: async (id) => {
    return await apiInstance.delete(`/assets/linked-assets/${id}`);
  },
  addAssetMaintenance: async (body) => {
    return await apiInstanceFileUpload.post(`/assets/maintenance`, body);
  },
  getAssetMaintenance: async (fitlers) => {
    return await apiInstance.get(`/assets/maintenance`, {
      params: {
        ...fitlers,
      },
    });
  },
  getAssetMaintenanceCalendar: async (fitlers) => {
    return await apiInstance.get(`/assets/maintenance/calendar`, {
      params: {
        ...fitlers,
      },
    });
  },
  getAssetMaintenanceCalendarView: async (fitlers) => {
    return await apiInstance.get(`/assets/maintenance/calendar/view`, {
      params: {
        ...fitlers,
      },
    });
  },
  getAssetMaintenanceSummary: async (fitlers) => {
    return await apiInstance.get(`/assets/maintenance/summary`, {
      params: {
        ...fitlers,
      },
    });
  },
  deleteAssetMaintenance: async (id) => {
    return await apiInstance.delete(`/assets/maintenance/${id}`);
  },
  updateStatus: async (body) => {
    return await apiInstance.put(`/assets/maintenance/status`, body);
  },
  assetMaintenanceMarkComplete: async (body) => {
    return await apiInstance.put(`/assets/maintenance/status/complete`, body);
  },
};

export const RoleService = {
  createRole: async (body) => {
    return await apiInstance.post("/roles", body);
  },

  getRoles: async () => {
    return await apiInstance.get(`/roles`);
  },

  editRole: async (roleId, body) => {
    return await apiInstance.put(`/roles/${roleId}`, body);
  },
  changeUserRole: async (body) => {
    return await apiInstance.put(`/roles/update-user-role`, body);
  },
};

export const DepartmentService = {
  createDepartment: async (body) => {
    return await apiInstance.post("/department", body);
  },

  getDepartments: async () => {
    return await apiInstance.get(`/department`);
  },

  editDepartment: async (body) => {
    return await apiInstance.put(`/department`, body);
  },

  deleteDepartment: async (departmentId) => {
    return await apiInstance.delete(`/department/${departmentId}`);
  },

  addUserDepartmet: async (body) => {
    return await apiInstance.post(`/department/add-user-department`, body);
  },
};

export const QualificationService = {
  createQualification: async (body) => {
    return await apiInstance.post("/qualifications", body);
  },

  getQualifications: async () => {
    return await apiInstance.get(`/qualifications`);
  },

  editQualification: async (body) => {
    return await apiInstance.put(`/qualifications`, body);
  },

  deleteQualification: async (qualificationId) => {
    return await apiInstance.delete(`/qualifications/${qualificationId}`);
  },
  changeQualification: async (body) => {
    return await apiInstance.put(`/qualifications/update-user-qualification`, body);
  },
};
